// Theme loading + scene-side application. Fetches a theme (TOML or JSON),
// normalizes it through pypicToMagviz so all downstream consumers see the
// {ui, scene, axes, cycle, colormaps} shape, updates renderer/scene
// background, themes the coordinate-system overlay, and dispatches a
// 'ui:theme:apply' intent so the active UI driver restyles its own chrome.

import * as THREE from 'three';
import { parse as parseToml } from 'smol-toml';
import { store } from '../store/instance';
import { INTENTS } from '../store/intents';
import { pypicToMagviz, synthCycleColormap } from './pypicThemeMap';
import { applyThemeToSelectionVisuals } from '../scene/selection';
import type { ThemeDefaults } from '../config/defaults';
import type { ViewerContext } from './context';
import type { ColormapStop } from '../physics/types';

const THEME_CYCLE_COLORMAP = 'Theme Cycle';

type PartialTheme = Partial<ThemeDefaults>;

export function mergeTheme(base: PartialTheme | null | undefined, patch: PartialTheme | null | undefined): ThemeDefaults {
    const baseColormaps = (base && base.colormaps) || { sequential: null, diverging: null };
    const patchColormaps = (patch && patch.colormaps) || {};
    return {
        name: (patch && patch.name) || (base && base.name) || null,
        ui: { ...((base && base.ui) || {}), ...((patch && patch.ui) || {}) } as ThemeDefaults['ui'],
        font: { ...((base && base.font) || {}), ...((patch && patch.font) || {}) } as ThemeDefaults['font'],
        scene: {
            ...((base && base.scene) || {}),
            ...((patch && patch.scene) || {}),
            grid: { ...((base && base.scene && base.scene.grid) || {}), ...((patch && patch.scene && patch.scene.grid) || {}) },
            selection: { ...((base && base.scene && base.scene.selection) || {}), ...((patch && patch.scene && patch.scene.selection) || {}) },
        } as ThemeDefaults['scene'],
        axes: { ...((base && base.axes) || {}), ...((patch && patch.axes) || {}) } as ThemeDefaults['axes'],
        cycle: (patch && Array.isArray(patch.cycle)) ? patch.cycle.slice() : ((base && base.cycle) || []),
        colormaps: { ...baseColormaps, ...patchColormaps } as ThemeDefaults['colormaps'],
    };
}

export async function fetchTheme(themeUrl: string): Promise<PartialTheme> {
    const res = await fetch(themeUrl, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    if (themeUrl.toLowerCase().endsWith('.toml')) {
        const text = await res.text();
        return pypicToMagviz(parseToml(text) as Record<string, unknown>);
    }
    return await res.json() as PartialTheme;
}

interface ApplyThemeSceneCtx {
    THREE: typeof import('three');
    renderer?: THREE.WebGLRenderer | null | undefined;
    scene?: THREE.Scene | null | undefined;
}

export function applyThemeToScene(theme: ThemeDefaults | null, { THREE, renderer, scene }: ApplyThemeSceneCtx): void {
    if (!theme || !theme.scene || !theme.scene.background || !THREE) return;
    const bg = new THREE.Color(theme.scene.background);
    if (renderer) renderer.setClearColor(bg, 1);
    if (scene) scene.background = bg;
}

interface LoadThemeCtx {
    baseTheme: PartialTheme;
    THREE: typeof import('three');
    renderer?: THREE.WebGLRenderer | null | undefined;
    scene?: THREE.Scene | null | undefined;
    debug?: boolean | undefined;
}

/**
 * High-level: fetch a theme URL, merge into the base, dispatch UI restyle,
 * update scene background, and theme the coordinate overlay.
 */
export async function loadTheme(themeUrl: string, ctx: LoadThemeCtx): Promise<ThemeDefaults | null> {
    if (!themeUrl) return null;
    try {
        const patch = await fetchTheme(themeUrl);
        const merged = mergeTheme(ctx.baseTheme || {}, patch);
        store.dispatch(INTENTS.UI_THEME_APPLY, { theme: merged });
        applyThemeToScene(merged, { THREE: ctx.THREE, renderer: ctx.renderer, scene: ctx.scene });
        return merged;
    } catch (e) {
        if (ctx.debug) console.warn('Failed to load theme', themeUrl, e);
        return null;
    }
}

/**
 * Load a theme URL and store the merged result on viewer.theme.current.
 * Also registers a synthesized 'Theme Cycle' colormap (from theme.cycle) into
 * viewer.colormap.defs so the cycle is selectable from the colormap dropdown.
 */
export async function applyTheme(viewer: ViewerContext, themeUrl: string): Promise<ThemeDefaults | null> {
    const merged = await loadTheme(themeUrl, {
        baseTheme: viewer.theme.current || {},
        THREE,
        renderer: viewer.three.renderer,
        scene: viewer.three.scene,
        debug: viewer.sceneMeta.debugEnabled,
    });
    if (!merged) return null;
    viewer.theme.current = merged;
    if (viewer.colormap && viewer.colormap.defs) {
        const cmap = synthCycleColormap(merged.cycle, THEME_CYCLE_COLORMAP) as ColormapStop[] | null;
        if (cmap) viewer.colormap.defs[THEME_CYCLE_COLORMAP] = cmap;
        else delete viewer.colormap.defs[THEME_CYCLE_COLORMAP];
    }
    try { applyThemeToSelectionVisuals(viewer); } catch { /* swallow */ }
    return merged;
}
